// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AdminJobForm.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI } from '../../services/api';
import './AdminJobForm.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Cancel: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Plus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  Remove: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ajf-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

const AdminJobForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!id);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    job_type: 'full_time',
    experience_required: '',
    description: '',
    requirements: [''],
    is_active: true,
  });

  useEffect(() => {
    if (id) {
      fetchJob();
    }
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await jobsAPI.getJobDetail(parseInt(id!));
      const job = response.data;
      setFormData({
        title: job.title,
        department: job.department,
        location: job.location,
        job_type: job.job_type,
        experience_required: job.experience_required,
        description: job.description,
        requirements: job.requirements.length ? job.requirements : [''],
        is_active: job.is_active,
      });
    } catch (error) {
      console.error('Error fetching job:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRequirementChange = (index: number, value: string) => {
    const newRequirements = [...formData.requirements];
    newRequirements[index] = value;
    setFormData({ ...formData, requirements: newRequirements });
  };

  const addRequirement = () => {
    setFormData({
      ...formData,
      requirements: [...formData.requirements, ''],
    });
  };

  const removeRequirement = (index: number) => {
    const newRequirements = formData.requirements.filter((_, i) => i !== index);
    setFormData({ ...formData, requirements: newRequirements });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const filteredRequirements = formData.requirements.filter(req => req.trim() !== '');
    
    if (filteredRequirements.length === 0) {
      alert('Please add at least one requirement');
      return;
    }
    
    const submitData = {
      ...formData,
      requirements: filteredRequirements,
    };
    
    setLoading(true);
    try {
      if (id) {
        await jobsAPI.updateJob(parseInt(id!), submitData);
      } else {
        await jobsAPI.createJob(submitData);
      }
      navigate('/dashboard/admin/jobs');
    } catch (error) {
      console.error('Error saving job:', error);
      alert('Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="ajf-loading-container">
        <div className="ajf-spinner">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ajf-spinner-svg">
            <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <p className="ajf-loading-text">Loading job details...</p>
      </div>
    );
  }

  return (
    <div className="ajf-container">
      {/* Header */}
      <div className="ajf-header">
        <div className="ajf-header-left">
          <button className="ajf-back-btn" onClick={() => navigate('/dashboard/admin/jobs')}>
            <Icons.Back />
            Back to Jobs
          </button>
          <h1 className="ajf-title">{id ? 'Edit Job' : 'Create New Job'}</h1>
        </div>
        <button className="ajf-btn-secondary" onClick={() => navigate('/dashboard/admin/jobs')}>
          <Icons.Cancel />
          Cancel
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="ajf-form">
        <div className="ajf-form-grid">
          {/* Left Column */}
          <div className="ajf-form-left">
            <div className="ajf-card">
              <h3 className="ajf-card-title">Job Information</h3>
              
              <div className="ajf-form-group">
                <label className="ajf-form-label">Job Title *</label>
                <input
                  type="text"
                  name="title"
                  className="ajf-form-input"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>

              <div className="ajf-form-group">
                <label className="ajf-form-label">Department *</label>
                <input
                  type="text"
                  name="department"
                  className="ajf-form-input"
                  required
                  value={formData.department}
                  onChange={handleInputChange}
                  placeholder="e.g., Engineering"
                />
              </div>

              <div className="ajf-form-group">
                <label className="ajf-form-label">Location *</label>
                <input
                  type="text"
                  name="location"
                  className="ajf-form-input"
                  required
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., Mwanza, Tanzania or Remote"
                />
              </div>

              <div className="ajf-form-group">
                <label className="ajf-form-label">Job Description *</label>
                <textarea
                  name="description"
                  className="ajf-form-textarea"
                  required
                  rows={5}
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the role, responsibilities, and what the candidate will do..."
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="ajf-form-right">
            <div className="ajf-card">
              <h3 className="ajf-card-title">Settings</h3>
              
              <div className="ajf-form-group">
                <label className="ajf-form-label">Job Type *</label>
                <select
                  name="job_type"
                  className="ajf-form-select"
                  required
                  value={formData.job_type}
                  onChange={handleInputChange}
                >
                  <option value="full_time">Full-time</option>
                  <option value="part_time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="remote">Remote</option>
                </select>
              </div>

              <div className="ajf-form-group">
                <label className="ajf-form-label">Experience Required *</label>
                <input
                  type="text"
                  name="experience_required"
                  className="ajf-form-input"
                  required
                  value={formData.experience_required}
                  onChange={handleInputChange}
                  placeholder="e.g., 5+ years"
                />
              </div>

              <div className="ajf-form-group ajf-form-checkbox">
                <label className="ajf-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span className="ajf-checkbox-text">Active (visible to applicants)</span>
                </label>
              </div>
            </div>

            {/* Requirements */}
            <div className="ajf-card">
              <h3 className="ajf-card-title">Requirements *</h3>
              {formData.requirements.map((req, index) => (
                <div key={index} className="ajf-requirement-input">
                  <input
                    type="text"
                    className="ajf-form-input"
                    value={req}
                    onChange={(e) => handleRequirementChange(index, e.target.value)}
                    placeholder={`Requirement ${index + 1}`}
                  />
                  {formData.requirements.length > 1 && (
                    <button 
                      type="button" 
                      className="ajf-remove-req" 
                      onClick={() => removeRequirement(index)}
                      title="Remove requirement"
                    >
                      <Icons.Remove />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="ajf-add-req-btn" onClick={addRequirement}>
                <Icons.Plus />
                Add Requirement
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="ajf-form-actions">
          <button 
            type="submit" 
            className="ajf-btn-primary" 
            disabled={loading}
          >
            {loading ? (
              <>
                <Icons.Spinner />
                Saving...
              </>
            ) : (
              <>
                <Icons.Save />
                {id ? 'Update Job' : 'Create Job'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminJobForm;